import React from 'react';
import { Box } from '@mui/material';

interface ConeProps {
  radius?: number;
  height?: number;
  slantHeight?: number;
  unit?: string;
  color?: string;
  strokeColor?: string;
}

export const Cone: React.FC<ConeProps> = ({
  radius = 3,
  height = 5,
  slantHeight,
  unit = 'cm',
  color = '#fef3c7',
  strokeColor = '#9c27b0'
}) => {
  const cx = 50;
  const cy = 55;
  
  const maxDim = Math.max(radius * 2, height || slantHeight || 5);
  const scale = 45 / maxDim;
  
  const R = radius * scale;
  // If height is not provided, calculate it from slantHeight
  const hValue = height !== undefined ? height : Math.sqrt(slantHeight! * slantHeight! - radius * radius);
  const H = hValue * scale;
  
  const rx = R * Math.sqrt(1.5);
  const ry = R * Math.SQRT1_2;
  
  const bottomY = cy + H / 2;
  const apexY = cy - H / 2;

  const sin_t = ry / H; 
  const valid_sin = sin_t < 1 ? sin_t : 0;
  const cos_t = Math.sqrt(1 - valid_sin * valid_sin);
  
  const tanX = rx * cos_t;
  const tanY = ry * valid_sin; 
  
  const leftX = cx - tanX;
  const rightX = cx + tanX;
  const tangentY = bottomY + tanY;

  const bodyPath = `M ${cx} ${apexY} L ${leftX} ${tangentY} A ${rx} ${ry} 0 0 0 ${rightX} ${tangentY} Z`;
  const backBasePath = `M ${leftX} ${tangentY} A ${rx} ${ry} 0 0 1 ${rightX} ${tangentY}`;
  const frontBasePath = `M ${leftX} ${tangentY} A ${rx} ${ry} 0 0 0 ${rightX} ${tangentY}`;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', maxWidth: '320px', height: '320px', display: 'block' }}
      >
        <defs>
          <linearGradient id="coneGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={color} stopOpacity={0.7} />
            <stop offset="50%" stopColor={color} stopOpacity={0.85} />
            <stop offset="100%" stopColor={color} stopOpacity={1.0} />
          </linearGradient>
        </defs>

        {/* Hidden edges (dashed) */}
        <path d={backBasePath} fill="none" stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        
        {/* Center line (height) */}
        {height !== undefined && slantHeight === undefined && (
          <line x1={cx} y1={bottomY} x2={cx} y2={apexY} stroke={strokeColor} strokeWidth={1} strokeDasharray="1,2" opacity={0.6} />
        )}

        {/* Cone Body */}
        <path d={bodyPath} fill="url(#coneGrad)" />

        {/* Visible Edges */}
        <path d={`M ${cx} ${apexY} L ${leftX} ${tangentY}`} fill="none" stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" />
        <path d={`M ${cx} ${apexY} L ${rightX} ${tangentY}`} fill="none" stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" />
        <path d={frontBasePath} fill="none" stroke={strokeColor} strokeWidth={1.5} />

        {/* Corner points */}
        <circle cx={cx} cy={apexY} r={1.2} fill={strokeColor} />
        <circle cx={cx} cy={bottomY} r={1.2} fill={strokeColor} />
        
        {/* Dimension labels */}
        {/* r (radius) */}
        <line x1={cx} y1={bottomY} x2={cx + rx} y2={bottomY} stroke={strokeColor} strokeWidth={1} strokeDasharray="1,2" opacity={0.6} />
        <text x={cx + rx / 2} y={bottomY - 2} fontSize={3.5} fill="#374151" textAnchor="middle" fontWeight={600}>r = {radius} {unit}</text>

        {/* h or l */}
        {height !== undefined && (
          <text x={cx - 3} y={cy} fontSize={3.5} fill="#374151" textAnchor="end" fontWeight={600}>h = {height} {unit}</text>
        )}
        {slantHeight !== undefined && height === undefined && (
          <text x={(cx + rightX) / 2 + 3} y={(apexY + tangentY) / 2} fontSize={3.5} fill="#374151" textAnchor="start" fontWeight={600}>l = {slantHeight} {unit}</text>
        )}
      </svg>
    </Box>
  );
};

export default Cone;
